import React, { useCallback } from 'react';
import {
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Button,
  Fade,
  Tooltip,
  SvgIcon,
  styled,
  Chip,
} from '@mui/material';
import {
  ContentCopy as DuplicateIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import * as MuiIcons from '@mui/icons-material';

import { ImpactAiPreset } from '../types';

const PresetIcon = styled(SvgIcon)(({ theme }) => ({
  fontSize: 20,
  marginRight: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

const DynamicIcon = styled(SvgIcon, {
  shouldForwardProp: (prop) => prop !== 'iconColor',
})<{ iconColor: string }>(({ iconColor }) => ({
  fontSize: 20,
  marginRight: 8,
  color: iconColor,
}));

interface PresetsListProps {
  presets: ImpactAiPreset[];
  selectedPreset: Partial<ImpactAiPreset> | null;
  hoveredPresetId: string | null;
  isAnyOperationLoading: boolean;
  onSelectPreset: (preset: ImpactAiPreset) => void;
  onCreateNew: () => void;
  onDuplicate: (preset: ImpactAiPreset, event: React.MouseEvent) => void;
  onDelete: (preset: ImpactAiPreset, event: React.MouseEvent) => void;
  onHoverPreset: (id: string | null) => void;
}

const PresetsList: React.FC<PresetsListProps> = ({
  presets,
  selectedPreset,
  hoveredPresetId,
  isAnyOperationLoading,
  onSelectPreset,
  onCreateNew,
  onDuplicate,
  onDelete,
  onHoverPreset,
}) => {
  const handleSelectPreset = useCallback(
    (preset: ImpactAiPreset) => () => {
      onSelectPreset(preset);
    },
    [onSelectPreset]
  );

  const handleHoverEnter = useCallback(
    (id: string) => () => {
      onHoverPreset(id);
    },
    [onHoverPreset]
  );

  const handleHoverLeave = useCallback(() => {
    onHoverPreset(null);
  }, [onHoverPreset]);

  const handleDuplicate = useCallback(
    (preset: ImpactAiPreset) => (e: React.MouseEvent) => {
      onDuplicate(preset, e);
    },
    [onDuplicate]
  );

  const handleDelete = useCallback(
    (preset: ImpactAiPreset) => (e: React.MouseEvent) => {
      onDelete(preset, e);
    },
    [onDelete]
  );

  return (
    <Paper
      elevation={0}
      sx={{
        width: 350,
        borderRight: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box p={2} borderBottom="1px solid #e0e0e0">
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onCreateNew}
          fullWidth
          disabled={isAnyOperationLoading}
        >
          Create New Preset
        </Button>
      </Box>

      <Box flex={1} overflow="auto">
        <List disablePadding>
          {presets.map((preset) => {
            const IconComponent = (MuiIcons as Record<string, React.ComponentType>)[
              preset.iconName
            ];

            return (
              <ListItem
                key={preset.id}
                button
                selected={selectedPreset?.id === preset.id}
                onClick={handleSelectPreset(preset)}
                onMouseEnter={handleHoverEnter(preset.id)}
                onMouseLeave={handleHoverLeave}
                sx={{
                  borderBottom: '1px solid #f0f0f0',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {IconComponent ? (
                    <DynamicIcon as={IconComponent} iconColor={preset.color || '#6366f1'} />
                  ) : (
                    <PresetIcon sx={{ color: preset.color || '#6366f1' }}>?</PresetIcon>
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      {preset.title}
                      {preset.enabled === false && (
                        <Chip label="Hidden" size="small" color="default" />
                      )}
                    </Box>
                  }
                  secondary={preset.description}
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: selectedPreset?.id === preset.id ? 600 : 400,
                  }}
                  secondaryTypographyProps={{
                    fontSize: '0.8rem',
                    noWrap: true,
                  }}
                />
                <Fade in={hoveredPresetId === preset.id}>
                  <Box display="flex" gap={0.5}>
                    <Tooltip title="Duplicate preset">
                      <IconButton
                        size="small"
                        onClick={handleDuplicate(preset)}
                        disabled={isAnyOperationLoading}
                        sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                      >
                        <DuplicateIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete preset">
                      <IconButton
                        size="small"
                        onClick={handleDelete(preset)}
                        disabled={isAnyOperationLoading}
                        sx={{ opacity: 0.7, '&:hover': { opacity: 1, color: 'error.main' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Fade>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Paper>
  );
};

export default React.memo(PresetsList);
